import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Wallet, Download } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function monthLabel(m) {
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

export default function MyPayslips() {
  const { data, isLoading } = useQuery({ queryKey: ["myPayroll"], queryFn: async () => (await api.get("/my/payroll")).data });

  const download = async (month) => {
    try {
      const res = await api.get(`/my/payroll/${month}/payslip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Payslip_${month}.pdf`; a.click();
    } catch { toast.error("Unable to download this payslip right now"); }
  };

  const months = data?.available_months || [];
  const structure = data?.current_structure;

  return (
    <div>
      <PageHeader overline="Compensation" title="My Payslips" icon={Wallet}
        subtitle="Payslips appear here once HR has finalized (locked) that month's payroll." />

      {structure && (
        <Card className="border-slate-200 shadow-none p-5 mb-6">
          <p className="overline text-slate-400 mb-2">Current Salary Structure</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2">
            <div><span className="text-xs text-slate-400 block">Gross / month</span><span className="text-navy font-semibold">{inr(structure.gross)}</span></div>
            <div><span className="text-xs text-slate-400 block">Basic</span><span className="text-navy">{inr(structure.basic)}</span></div>
            <div><span className="text-xs text-slate-400 block">HRA</span><span className="text-navy">{inr(structure.hra)}</span></div>
            <div><span className="text-xs text-slate-400 block">Travelling Allowance</span><span className="text-navy">{inr(structure.travelling)}</span></div>
          </div>
        </Card>
      )}

      {isLoading ? <Loading /> : months.length === 0 ? (
        <EmptyState icon={Wallet} title="No payslips yet" hint="Your first payslip will appear here once HR finalizes a month's payroll." />
      ) : (
        <Card className="border-slate-200 shadow-none divide-y divide-slate-100">
          {months.map((m) => (
            <div key={m} className="flex items-center justify-between px-5 py-3.5" data-testid={`payslip-row-${m}`}>
              <span className="text-navy font-medium">{monthLabel(m)}</span>
              <Button size="sm" variant="outline" onClick={() => download(m)} data-testid={`download-payslip-${m}`}>
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
